import { ROUTER_API } from 'app/constants/api.constant';
import { STORAGE_KEYS } from 'app/constants/storage.constant';
import BaseApi from 'app/services/api/base.api';
import {
  ADMIN_HOME_OBJECTIVE_PATH,
  ADMIN_HOME_VIDEOS_PATH,
  HOME_LAUNCH_TASKS_PATH,
  HOME_OBJECTIVE_PATH,
  HOME_VIDEOS_PATH,
} from '../constants';
import {
  ChecklistItemType,
  HomeObjective,
  HomeObjectiveSettings,
  HomeVideo,
  HomeVideoInput,
  HomeVideoSection,
  LaunchTasks,
  LaunchTaskType,
  StoreHomeObjectiveAdmin,
  StoreHomeObjectiveOverride,
} from '../types';

export const homeApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 20000 });

// Never registered in jwtService: while an admin impersonates a merchant, the session
// token is the merchant's, and the admin routes need the admin's own token instead.
const impersonatingAdminApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 20000 });

export const adminClient = () => {
  const adminToken = localStorage.getItem(STORAGE_KEYS.JWT_IMPOSTOR_TOKEN);
  return adminToken
    ? { api: impersonatingAdminApi.api, params: { token: adminToken } }
    : { api: homeApi.api, params: {} };
};

export const fetchHomeObjectiveApi = async (storeId: string) => {
  const response = await homeApi.api.get<HomeObjective>(HOME_OBJECTIVE_PATH(storeId));
  return response.data;
};

// On the merchant's own token: an admin impersonating the merchant ticks as LittleBill,
// which the API reads from that token.
export const fetchLaunchTasksApi = async (storeId: string) => {
  const response = await homeApi.api.get<LaunchTasks>(HOME_LAUNCH_TASKS_PATH(storeId));
  return response.data;
};

export const setLaunchTaskCheckApi = async (storeId: string, type: LaunchTaskType, checked: boolean) => {
  const url = `${HOME_LAUNCH_TASKS_PATH(storeId)}/${type}/check`;
  const response = checked ? await homeApi.api.put<LaunchTasks>(url) : await homeApi.api.delete<LaunchTasks>(url);
  return response.data;
};

export const fetchHomeVideosApi = async () => {
  const response = await homeApi.api.get<HomeVideo[]>(HOME_VIDEOS_PATH);
  return response.data;
};

export const fetchCommonSettingsApi = async () => {
  const { api, params } = adminClient();
  const response = await api.get<HomeObjectiveSettings>(`${ADMIN_HOME_OBJECTIVE_PATH}/defaults`, { params });
  return response.data;
};

export const updateCommonSettingsApi = async (settings: HomeObjectiveSettings) => {
  const { api, params } = adminClient();
  const response = await api.put<HomeObjectiveSettings>(`${ADMIN_HOME_OBJECTIVE_PATH}/defaults`, settings, {
    params,
  });
  return response.data;
};

const storeAdminPath = (storeId: string) => `${ADMIN_HOME_OBJECTIVE_PATH}/stores/${storeId}`;

export const fetchStoreAdminViewApi = async (storeId: string) => {
  const { api, params } = adminClient();
  const response = await api.get<StoreHomeObjectiveAdmin>(storeAdminPath(storeId), { params });
  return response.data;
};

export const updateStoreOverrideApi = async (storeId: string, override: StoreHomeObjectiveOverride) => {
  const { api, params } = adminClient();
  const response = await api.put<StoreHomeObjectiveAdmin>(storeAdminPath(storeId), override, { params });
  return response.data;
};

export const resetStoreOverrideApi = async (storeId: string) => {
  const { api, params } = adminClient();
  const response = await api.delete<StoreHomeObjectiveAdmin>(storeAdminPath(storeId), { params });
  return response.data;
};

export const setManualValidationApi = async (storeId: string, type: ChecklistItemType, validated: boolean) => {
  const { api, params } = adminClient();
  const url = `${storeAdminPath(storeId)}/items/${type}/manual-validation`;
  const response = validated
    ? await api.put<StoreHomeObjectiveAdmin>(url, undefined, { params })
    : await api.delete<StoreHomeObjectiveAdmin>(url, { params });
  return response.data;
};

export const fetchAllHomeVideosApi = async () => {
  const { api, params } = adminClient();
  const response = await api.get<HomeVideo[]>(ADMIN_HOME_VIDEOS_PATH, { params });
  return response.data;
};

export const createHomeVideoApi = async (video: HomeVideoInput) => {
  const { api, params } = adminClient();
  const response = await api.post<HomeVideo>(ADMIN_HOME_VIDEOS_PATH, video, { params });
  return response.data;
};

export const updateHomeVideoApi = async (videoId: string, video: Partial<HomeVideoInput>) => {
  const { api, params } = adminClient();
  const response = await api.patch<HomeVideo>(`${ADMIN_HOME_VIDEOS_PATH}/${videoId}`, video, { params });
  return response.data;
};

export const deleteHomeVideoApi = async (videoId: string) => {
  const { api, params } = adminClient();
  await api.delete(`${ADMIN_HOME_VIDEOS_PATH}/${videoId}`, { params });
};

export const reorderHomeVideosApi = async (section: HomeVideoSection, videoIds: string[]) => {
  const { api, params } = adminClient();
  const response = await api.put<HomeVideo[]>(`${ADMIN_HOME_VIDEOS_PATH}/order`, { section, videoIds }, { params });
  return response.data;
};
